/**
 * CompanyHome.jsx – regenerated 29-Jul-2025
 * ---------------------------------------------------------------
 * Stand-alone dashboard with FULL CRUD for users, leases, wells, tanks,
 * audit logs, **and production/well-test charts** (new “Charts” tab).
 * Route: /companyHome/:subdomain
 * ---------------------------------------------------------------
 */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faEye,
  faEyeSlash,
  faSearch,
  faPlus,
  faEdit,
  faUserEdit,
  faCopy,
  faTrash,
  faChevronUp,
  faChevronDown,
  faTimes,
  faSortUp,
  faSortDown,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme, useUser } from "ogcommon";
import UsersTab from "./UsersTab";
import ExplorerModal from "./ExplorerModal";
import ScheduledReportsPanel from "./ScheduledReports"; // ← NEW

export default function CompanyHome() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { userRole, userID, setUser, companyName } = useUser(); // no subdomain from context
  const { subdomain } = useParams(); // ← now only source for subdomain
  const [jobTypes, setJobTypes] = useState([]);
  const CompanyLogs = React.lazy(() => import("./CompanyLogs"));

  /* ───────────── top-level state ───────────── */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState("logs"); // default tab

  /* slices editable in-page */
  const [users, setUsers] = useState([]);
  const [leases, setLeases] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  const [tags, setTags] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [showExplorer, setShowExplorer] = useState(false); // ⬅️ new

  /* filters */
  const [userFilter, setUserFilter] = useState("");
  const [leaseFilter, setLeaseFilter] = useState("");
  const [useFieldTicket, setUseFieldTicket] = useState("");

  const [showActiveLeases, setShowActiveLeases] = useState(true);
  const [hasOgFiles, setOgFiles] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tokenAccess = searchParams.has("token"); // or check for a specific param/key

  const srcHint = (searchParams.get("src") || "").toLowerCase();
  const fromOgFieldTicket = (() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    const src = (params.get("src") || "").toLowerCase();
    return src === "fieldticket" || src === "ogfieldticket";
  })();

  /* ───────────── fetch payload once ───────────── */
  const fetchCompany = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const r = await fetch(
        `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          subdomain || "",
        )}`,
      );
      const js = await r.json();
      if (!Array.isArray(js) || !js.length) throw new Error("Client not found");
      const single = js[0];

      // parse perms JSON (hasOgFiles, UseFieldTicket)
      let perms = {};
      if (typeof single.perms === "string") {
        try {
          perms = JSON.parse(single.perms);
        } catch (e) {
          console.warn("Invalid perms JSON", e);
        }
      } else if (single.perms && typeof single.perms === "object") {
        perms = single.perms;
      }

      setCompany(single);
      setOgFiles(!!perms.hasOgFiles);
      setUseFieldTicket(!!perms.UseFieldTicket);
      setJobTypes(Array.isArray(single.jobTypes) ? single.jobTypes : []);
      setUsers(Array.isArray(single.users) ? single.users : []);
      setLeases(Array.isArray(single.leases) ? single.leases : []);
      setPurchasers(Array.isArray(single.purchasers) ? single.purchasers : []);
      setTags(Array.isArray(single.tags) ? single.tags : []);
      setAuditLog(Array.isArray(single.auditLog) ? single.auditLog : []);
    } catch (err) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [subdomain]);

  useEffect(() => {
    if (company && auditLog.length === 0) {
      setActiveTab("leases"); // or "users"
    }
  }, [company, auditLog]);

  useEffect(() => {
    setShowExplorer(activeTab === "explorer");
  }, [activeTab]);

  const baseTabs = [
    { id: "leases", label: `Leases (${leases.length})` },
    { id: "users", label: `Users (${users.length})` },
    { id: "logs", label: "Audit Logs" }, // ← always present
    { id: "schedules", label: "Scheduled Reports" }, // ← NEW Tab
  ];

  // only add “Explorer” if hasOgFiles is true
  const tabs = hasOgFiles
    ? [...baseTabs, { id: "explorer", label: "Explorer" }]
    : baseTabs;

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  /* ───────────── derived lists ───────────── */
  const filteredUsers = useMemo(() => {
    const q = userFilter.toLowerCase();
    return users.filter(
      (u) =>
        u.UserID.toLowerCase().includes(q) ||
        (u.FullName || "").toLowerCase().includes(q) ||
        (u.Email || "").toLowerCase().includes(q),
    );
  }, [users, userFilter]);

  const filteredLeases = useMemo(() => {
    const q = leaseFilter.toLowerCase();
    return leases
      .filter((l) => (showActiveLeases ? l.Active === "Y" : l.Active !== "Y"))
      .filter(
        (l) =>
          l.LeaseID.toLowerCase().includes(q) ||
          (l.LeaseName || "").toLowerCase().includes(q) ||
          (l.RRC || "").toLowerCase().includes(q),
      );
  }, [leases, leaseFilter, showActiveLeases]);

  /* ───────────── theme helpers ───────────── */
  const clsCard =
    theme === "dark"
      ? "bg-gray-800 text-gray-100 border-gray-700"
      : "bg-white text-gray-800 border-gray-200";

  const clsInput =
    theme === "dark"
      ? "bg-gray-700 text-gray-100 border-gray-600"
      : "bg-white text-gray-800 border-gray-300";

  /* ───────────── loading / error guards ───────────── */
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  if (error)
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-red-600 text-lg font-semibold">{error}</p>
      </div>
    );
  if (!company) return null;

  /* ───────────── render ───────────── */
  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        theme === "dark"
          ? "bg-gray-900 text-gray-100"
          : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* header */}
        <div
          className={`p-6 mb-6 rounded-xl shadow-md border ${clsCard} bg-gradient-to-r from-blue-500 to-indigo-500 bg-opacity-80`}
        >
          <h1 className="text-3xl font-bold text-white mb-1">
            {company.company}
          </h1>

          <p className="text-white/80">
            {tokenAccess ? (
              fromOgFieldTicket ? (
                <a
                  href={`https://${company.subdomain}.ogfieldticket.ogpumper.net`}
                  target="_top"
                  rel="noopener"
                  className="underline font-mono"
                >
                  Return to ogFieldTicket
                </a>
              ) : (
                <a
                  href={`https://${company.subdomain}.${company.AppName}.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-mono"
                >
                  Return to ogPumper
                </a>
              )
            ) : (
              <>
                <a
                  href={`https://${company.subdomain}.${company.AppName}.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-mono"
                >
                  {company.subdomain}.{company.AppName}.com
                </a>
                <p className="mt-1">
                  Status:{" "}
                  <span
                    className={
                      company.Active === "Y" ? "text-green-300" : "text-red-300"
                    }
                  >
                    {company.Active === "Y" ? "Active" : "Inactive"}
                  </span>
                </p>
              </>
            )}
          </p>
        </div>

        {/* tabs */}
        <div className="mb-4 flex flex-wrap gap-2 border-b pb-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 rounded-t-lg font-semibold transition-colors ${
                activeTab === t.id
                  ? "border-b-2 border-blue-500 text-blue-700"
                  : theme === "dark"
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ───── Users ───── */}
        {activeTab === "users" && (
          <UsersTab
            users={filteredUsers}
            setUsers={setUsers}
            theme={theme}
            companyName={subdomain}
            userFilter={userFilter}
            setUserFilter={setUserFilter}
            clsInput={clsInput}
            clsCard={clsCard}
            role={userRole.role}
            useFieldTicket={useFieldTicket ? "Y" : "N"} // ← use instantiated perms
            jobTypes={jobTypes}
          />
        )}

        {/* ───── Leases ───── */}
        {activeTab === "leases" && (
          <LeasesTab
            leases={leases}
            setLeases={setLeases}
            users={users}
            purchasers={purchasers}
            tags={tags}
            theme={theme}
            companyName={subdomain}
            searchTerm={leaseFilter}
            setSearchTerm={setLeaseFilter}
            showActive={showActiveLeases}
            setShowActive={setShowActiveLeases}
            clsCard={clsCard}
          />
        )}

        {/* ───── Logs ───── */}
        {activeTab === "logs" && (
          <React.Suspense
            fallback={
              <div className="flex h-64 items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid"></div>
              </div>
            }
          >
            <CompanyLogs
              theme={theme}
              company={subdomain}
              apiBase="https://house.ogendeavors.com/api"
              pageSize={10000}
              auditSince=""
              auditSlim={false}
            />
          </React.Suspense>
        )}

        {/* ───── Schedules (NEW) ───── */}
        {activeTab === "schedules" && (
          <ScheduledReportsPanel
            subdomain={subdomain}
            users={users}
            leases={leases}
            tags={tags}
            theme={theme}
            baseUrl="https://house.ogendeavors.com/api"
          />
        )}

        {hasOgFiles && (
          <ExplorerModal
            open={showExplorer} // ✅ tells modal to render
            theme={theme} // ✅ for dark/light styles
            companyName={company.company} // just a label in the header
            onClose={() => setActiveTab("logs")}
          />
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
/*  INPUT CLASS HELPER                                          */
/* ───────────────────────────────────────────────────────────── */
const inputClasses = (theme, { readOnly = false } = {}) =>
  `w-full px-2 py-1 rounded border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    readOnly ? "opacity-70 cursor-not-allowed" : ""
  } ${
    theme === "dark"
      ? "bg-gray-700 text-gray-100 border-gray-600"
      : "bg-white text-gray-800 border-gray-300"
  }`;

/* ───────────────────────────────────────────────────────────── */
/*  USERS TAB (with full Create / Edit modal)                   */
/* ───────────────────────────────────────────────────────────── */

/* ───────────────────────────────────────────────────────────── */
/*  LEASES TAB (with full modal incl. Wells/Tanks)              */
/* ───────────────────────────────────────────────────────────── */
// ogsupport - move check mark on active leases to the right side, (normalize with users)
// Replace Pumper - filter where Role is P
// Replace Pumper - add an "...Are you sure .. " confirmation ...

// DB change required:
// ALTER TABLE wells ADD ElectricMeter varchar(50) NULL;

function LeasesTab({
  leases,
  setLeases,
  users,
  purchasers, // Passed in from props
  tags, // Passed in from props
  theme,
  companyName,
  subdomain,
}) {
  const [showLeaseModal, setShowLeaseModal] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [isEditMode, setIsEditMode] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "" });

  // For searching and sorting (leases grid)
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("LeaseID");
  const [sortDirection, setSortDirection] = useState("asc");

  // Show/Hide inactive leases (default Hide)
  const [showInactive, setShowInactive] = useState(false);

  // Replace Pumper modal
  const [showReplacePumperModal, setShowReplacePumperModal] = useState(false);
  const [replaceFromPumper, setReplaceFromPumper] = useState("");
  const [replaceToPumper, setReplaceToPumper] = useState("");
  const [replaceBusy, setReplaceBusy] = useState(false);

  // Valid pumpers
  const [validPumpers, setValidPumpers] = useState([]);

  // Wells grid state (in modal)
  const [wellSearch, setWellSearch] = useState("");
  const [wellSortBy, setWellSortBy] = useState("WellID");
  const [wellSortDirection, setWellSortDirection] = useState("asc");
  const [showInactiveWells, setShowInactiveWells] = useState(false);

  // Tanks grid state (in modal)
  const [tankSearch, setTankSearch] = useState("");
  const [tankSortBy, setTankSortBy] = useState("TankID");
  const [tankSortDirection, setTankSortDirection] = useState("asc");
  const [showInactiveTanks, setShowInactiveTanks] = useState(false);

  const isDark = theme === "dark";

  // -------------------------------
  // HELPER STYLES
  // -------------------------------
  const getInputClasses = (isDarkMode) =>
    isDarkMode
      ? "w-full px-2 py-1 bg-gray-700 text-gray-100 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      : "w-full px-2 py-1 bg-white text-gray-800 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";

  const getHeaderClasses = () =>
    `border-b ${
      isDark
        ? "bg-gray-700 text-gray-100 border-gray-600"
        : "bg-gray-100 text-gray-800 border-gray-200"
    }`;

  const getRowClasses = () =>
    `border-b ${
      isDark ? "border-gray-600 hover:bg-gray-600" : "hover:bg-gray-50"
    }`;

  // Helper to compute valid pumpers from full users list
  const computeValidPumpers = (allUsers = []) =>
    allUsers.filter((u) => {
      if (!u) return false;
      if (u.Disabled === "1") return false;

      const role = (u.Role || "").toUpperCase();
      // Exclude Investor and Read-only roles
      if (role === "I" || role === "R") return false;

      if (!u.perms) return false;

      let permsObj;
      try {
        permsObj = JSON.parse(u.perms);
      } catch (e) {
        return false;
      }

      const ogpumperRead = permsObj?.ogpumper?.read === true;
      const ogfieldticketRead = permsObj?.ogfieldticket?.read === true;

      // Must have read perms in at least one of these apps
      return ogpumperRead || ogfieldticketRead;
    });

  // Keep validPumpers in sync with users prop
  useEffect(() => {
    setValidPumpers(computeValidPumpers(users || []));
  }, [users]);

  // Reconcile active tab when WellType changes (INJ hides Tanks & Purchasers, shows Injection)
  useEffect(() => {
    if (!editingLease) return;
    const isInjection = (editingLease.WellType || "").toUpperCase() === "INJ";
    if (isInjection) {
      if (activeTab === "tanks" || activeTab === "purchasers") {
        setActiveTab("injection");
      }
    } else {
      if (activeTab === "injection") setActiveTab("basic");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingLease?.WellType]);

  // Blank lease object for "Add New"
  const blankLease = {
    LeaseID: "",
    LeaseName: "",
    GroupID: "",
    FieldName: "",
    County: "",
    PumperID: "",
    PumperRoute: "",
    ReliefID: "",
    District: "",
    RRC: "",
    WellType: "OIL",
    ShowOil: "Y",
    ShowWater: "Y",
    ShowGas: "Y",
    WaterMeter: "N",
    Active: "Y",

    // Purchasers
    Purchaser: "",
    PurchaserLeaseNo: "",
    WaterTransport: "",

    // Injection
    UIC: "",
    Target: 0, // Daily Target (int)
    MaxInj: 0, // int
    MaxPressure: 0, // int

    PropertyNum: "",
    CP: "",
    Tag1: "",
    Tag2: "",
    Tag3: "",
    Tag4: "",
    Investor1: "",
    Investor2: "",
    wells: [],
    tanks: [],
  };

  // -------------------------------
  // CREATE/EDIT/COPY LEASE
  // -------------------------------
  const handleCreateLease = () => {
    setEditingLease({ ...blankLease });
    setIsEditMode(false);
    setActiveTab("basic");
    setShowLeaseModal(true);

    // reset sub-grid state
    setWellSearch("");
    setTankSearch("");
  };

  const handleExportLeases = () => {
    try {
      if (!leases?.length) throw new Error("No leases to export");

      /* ───────────── helper ───────────── */
      const serialise = (val) => {
        if (Array.isArray(val)) {
          return val
            .map((el) => {
              if (el == null) return "";
              if (typeof el === "object")
                return el.name ?? el.id ?? JSON.stringify(el);
              return String(el);
            })
            .join("; ");
        }
        if (val && typeof val === "object") return JSON.stringify(val);
        return val ?? "";
      };

      /* ───────────── build CSV ───────────── */
      const headers = Object.keys(leases[0]);
      const csv = [
        headers.join(","),
        ...leases.map((lease) =>
          headers
            .map((h) => {
              const escaped = String(serialise(lease[h])).replace(/"/g, '""');
              return `"${escaped}"`;
            })
            .join(","),
        ),
      ].join("\n");

      /* ───────────── download ───────────── */
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leases_${companyName}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export leases. Check console for details.");
    }
  };

  const handleEditLease = (lease) => {
    const clone = JSON.parse(JSON.stringify(lease));

    if (!Array.isArray(clone.wells)) clone.wells = [];
    if (!Array.isArray(clone.tanks)) clone.tanks = [];

    // Ensure newer fields exist for older records
    if (typeof clone.UIC !== "string") clone.UIC = "";
    if (typeof clone.County !== "string") clone.County = "";
    if (typeof clone.PurchaserLeaseNo !== "string") clone.PurchaserLeaseNo = "";
    if (typeof clone.WaterTransport !== "string") clone.WaterTransport = "";

    // Ensure newer well fields exist for older records
    clone.wells = clone.wells.map((well) => ({
      ...well,
      ElectricMeter:
        typeof well?.ElectricMeter === "string" ? well.ElectricMeter : "",
    }));

    // ints (default 0)
    if (
      clone.Target === null ||
      clone.Target === undefined ||
      clone.Target === ""
    )
      clone.Target = 0;
    if (
      clone.MaxInj === null ||
      clone.MaxInj === undefined ||
      clone.MaxInj === ""
    )
      clone.MaxInj = 0;
    if (
      clone.MaxPressure === null ||
      clone.MaxPressure === undefined ||
      clone.MaxPressure === ""
    )
      clone.MaxPressure = 0;

    setEditingLease(clone);
    setIsEditMode(true);
    setActiveTab("basic");
    setShowLeaseModal(true);

    // reset sub-grid state
    setWellSearch("");
    setTankSearch("");
  };

  // Copy a lease minus wells/tanks arrays
  const handleCopyLease = (lease) => {
    const clone = JSON.parse(JSON.stringify(lease));
    delete clone.wells;
    delete clone.tanks;

    clone.LeaseID = `${clone.LeaseID}-COPY`;

    // Ensure new fields exist
    if (typeof clone.UIC !== "string") clone.UIC = "";
    if (typeof clone.WaterTransport !== "string") clone.WaterTransport = "";
    if (
      clone.Target === null ||
      clone.Target === undefined ||
      clone.Target === ""
    )
      clone.Target = 0;
    if (
      clone.MaxInj === null ||
      clone.MaxInj === undefined ||
      clone.MaxInj === ""
    )
      clone.MaxInj = 0;
    if (
      clone.MaxPressure === null ||
      clone.MaxPressure === undefined ||
      clone.MaxPressure === ""
    )
      clone.MaxPressure = 0;

    setEditingLease(clone);
    setIsEditMode(false);
    setActiveTab("basic");
    setShowLeaseModal(true);

    setWellSearch("");
    setTankSearch("");
  };

  const handleCancelLease = () => {
    setEditingLease(null);
    setShowLeaseModal(false);
  };

  // -------------------------------
  // SAVE HELPERS
  // -------------------------------
  const normalizeLeaseForSave = (lease, allUsers) => {
    const usersArr = allUsers || [];
    const allUserIds = new Set(usersArr.map((u) => u.UserID));
    const validPumperIds = new Set(
      computeValidPumpers(usersArr).map((u) => u.UserID),
    );

    const out = { ...lease };

    // ----- FK fields -----
    if (!out.PumperID || !validPumperIds.has(out.PumperID)) {
      throw new Error("Invalid PumperID");
    }

    ["ReliefID", "Investor1", "Investor2"].forEach((k) => {
      if (out[k] === "") out[k] = null;
      else if (out[k] && !allUserIds.has(out[k])) out[k] = null;
    });

    // ----- Y/N flags -> 'Y'/'N'
    [
      "ShowOil",
      "ShowWater",
      "ShowGas",
      "ShowLACT",
      "ShowTbg",
      "ShowCsg",
      "ShowStatic",
      "ShowDiff",
      "ShowFlowRate",
      "ShowChoke",
      "ShowMcfAccum",
      "ShowHoursOn",
      "GasLift",
      "GasMeter",
      "WaterMeter",
      "Active",
      "ExcludeFromPR",
      "PumpOnMeter",
    ].forEach((k) => {
      if (k in out) {
        const v = out[k];
        out[k] = v === "Y" ? "Y" : v === "N" ? "N" : v ? "Y" : "N";
      }
    });

    // ----- Trim to schema widths
    const trim = (s, n) => (typeof s === "string" ? s.slice(0, n) : s);
    out.LeaseName = trim(out.LeaseName, 35);
    out.GroupID = trim(out.GroupID, 20);
    out.Tag1 = out.Tag1 === "" ? null : trim(out.Tag1, 20);
    out.Tag2 = out.Tag2 === "" ? null : trim(out.Tag2, 20);
    out.Tag3 = out.Tag3 === "" ? null : trim(out.Tag3, 20);
    out.Tag4 = out.Tag4 === "" ? null : trim(out.Tag4, 20);
    out.RRC = trim(out.RRC, 6);
    out.API = trim(out.API, 30);
    out.FieldName = trim(out.FieldName, 100);
    out.Legal = trim(out.Legal, 255);
    out.PropertyNum = trim(out.PropertyNum, 10);
    out.CP = trim(out.CP, 10);
    out.UIC = trim(out.UIC, 9);
    out.County = trim(out.County, 50);
    out.PurchaserLeaseNo = trim(out.PurchaserLeaseNo, 50);
    out.WaterTransport = trim(out.WaterTransport, 50);

    // ----- Numerics
    ["PumperRoute", "ReportOrder"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
      else if (out[k] !== undefined) out[k] = parseInt(out[k], 10);
    });

    // ints (keep int)
    ["MaxInj", "MaxPressure", "Target"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
      else if (out[k] !== undefined) out[k] = parseInt(out[k], 10);
    });

    ["WIDecimal", "NetLeaseDecimal"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
    });

    ["lat", "lon"].forEach((k) => {
      if (out[k] === "" || out[k] === null || isNaN(+out[k])) delete out[k];
      else out[k] = String(out[k]);
    });

    return out;
  };

  const handleSaveLease = async () => {
    if (!editingLease) return;

    if (!editingLease.LeaseID || !editingLease.LeaseID.trim()) {
      alert("A LeaseID is required.");
      return;
    }
    if (!editingLease.LeaseName || !editingLease.LeaseName.trim()) {
      alert("A LeaseName is required.");
      return;
    }

    const existsLocally = leases.some(
      (l) => l.LeaseID === editingLease.LeaseID,
    );
    if (!isEditMode && existsLocally) {
      alert(
        `LeaseID "${editingLease.LeaseID}" already exists.  Pick a unique ID.`,
      );
      return;
    }

    try {
      const payload = normalizeLeaseForSave(editingLease, users);
      const isInjection =
        (payload.WellType || editingLease.WellType || "").toUpperCase() ===
        "INJ";

      // Always include wells if present
      if (Array.isArray(editingLease.wells) && editingLease.wells.length > 0) {
        payload.wells = editingLease.wells.map((w) => ({
          ...w,
          LeaseID: editingLease.LeaseID,
          ElectricMeter:
            typeof w?.ElectricMeter === "string"
              ? w.ElectricMeter.slice(0, 50)
              : "",
        }));
      }

      // Only include tanks for NON-INJ
      if (
        !isInjection &&
        Array.isArray(editingLease.tanks) &&
        editingLease.tanks.length > 0
      ) {
        payload.tanks = editingLease.tanks.map((t) => ({
          ...t,
          LeaseID: editingLease.LeaseID,
        }));
      }

      const isExisting = leases.some((l) => l.LeaseID === editingLease.LeaseID);
      let url, method;

      if (!isExisting) {
        method = "POST";
        url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName,
        )}&createLease=1`;
      } else {
        method = "PATCH";
        url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName,
        )}&lid=${encodeURIComponent(editingLease.LeaseID)}`;
      }

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await response.text();
        throw new Error(
          `Lease save failed. Status: ${response.status}. ${msg}`,
        );
      }

      // Update local state to mirror what was sent/saved
      let updatedLeases;
      if (!isExisting) {
        updatedLeases = [...leases, { ...editingLease, ...payload }];
      } else {
        updatedLeases = leases.map((l) =>
          l.LeaseID === editingLease.LeaseID ? { ...l, ...payload } : l,
        );
      }
      setLeases(updatedLeases);

      setShowLeaseModal(false);
    } catch (err) {
      console.error("Error saving lease:", err);
      alert(err.message || "Error saving lease. Check console for details.");
    }
  };

  // -------------------------------
  // WELLS / TANKS - ADD / REMOVE
  // -------------------------------
  const handleRemoveWell = async (well) => {
    if (!well.UniqID) {
      setEditingLease((prev) => ({
        ...prev,
        wells: (prev.wells || []).filter((w) => w !== well),
      }));
      return;
    }

    const confirmed = window.confirm(
      `Remove well "${well.WellID}" from server?`,
    );
    if (!confirmed) return;

    try {
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}&lid=${encodeURIComponent(
        editingLease.LeaseID,
      )}&wid=${encodeURIComponent(well.UniqID)}`;

      const resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Delete well failed: ${resp.status} - ${msg}`);
      }

      setEditingLease((prev) => ({
        ...prev,
        wells: (prev.wells || []).filter((w) => w.UniqID !== well.UniqID),
      }));

      setLeases((prevLeases) =>
        prevLeases.map((lease) => {
          if (lease.LeaseID === editingLease.LeaseID) {
            return {
              ...lease,
              wells: (lease.wells || []).filter(
                (w) => w.UniqID !== well.UniqID,
              ),
            };
          }
          return lease;
        }),
      );
    } catch (err) {
      console.error("Error removing well:", err);
      setToast({
        visible: true,
        message:
          "Well has historical data and cannot be removed.  Please set Active to No to disable.",
      });
    }
  };

  const handleRemoveTank = async (tank) => {
    if (!tank.UniqID) {
      setEditingLease((prev) => ({
        ...prev,
        tanks: (prev.tanks || []).filter((t) => t !== tank),
      }));
      return;
    }

    const confirmed = window.confirm(
      `Remove tank "${tank.TankID}" from server? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}&lid=${encodeURIComponent(
        editingLease.LeaseID,
      )}&tid=${encodeURIComponent(tank.UniqID)}`;

      const resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Delete tank failed: ${resp.status} - ${msg}`);
      }

      setEditingLease((prev) => ({
        ...prev,
        tanks: (prev.tanks || []).filter((t) => t.UniqID !== tank.UniqID),
      }));

      setLeases((prevLeases) =>
        prevLeases.map((lease) => {
          if (lease.LeaseID === editingLease.LeaseID) {
            return {
              ...lease,
              tanks: (lease.tanks || []).filter(
                (t) => t.UniqID !== tank.UniqID,
              ),
            };
          }
          return lease;
        }),
      );
    } catch (err) {
      console.error("Error removing tank:", err);
      setToast({
        visible: true,
        message:
          "Tank has historical data and cannot be removed.  Please set Active to No to disable.",
      });
    }
  };

  const handleAddWell = () => {
    setEditingLease((prev) => {
      const newWell = {
        UniqID: "",
        LeaseID: prev.LeaseID,
        WellID: "",
        Active: "Y",
        PropertyNum: "",
        AllocPct: "0.0000",
        RRCLeaseID: "",
        CP: "",
        ElectricMeter: "",
      };
      return { ...prev, wells: [newWell, ...(prev.wells || [])] };
    });
  };

  const handleAddTank = () => {
    setEditingLease((prev) => {
      const newTank = {
        UniqID: "",
        LeaseID: prev.LeaseID,
        TankID: "",
        Size: 0,
        BBLSperInch: "0.000",
        Active: "Y",
        TankType: "T",
        GasCoeff: "0.00",
        ExcludeDrawsFromProd: "N",
        WPTankNum: "",
      };
      return { ...prev, tanks: [newTank, ...(prev.tanks || [])] };
    });
  };

  // -------------------------------
  // FIELD HANDLERS
  // -------------------------------
  const handleLeaseFieldChange = (e) => {
    const { name, value } = e.target;
    setEditingLease((prev) => ({ ...prev, [name]: value }));
  };

  const handleLeaseCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setEditingLease((prev) => ({ ...prev, [name]: checked ? "Y" : "N" }));
  };

  const handleWellChange = (index, field, value) => {
    setEditingLease((prev) => {
      const newWells = [...(prev.wells || [])];
      newWells[index] = { ...newWells[index], [field]: value };
      return { ...prev, wells: newWells };
    });
  };

  const handleTankChange = (index, field, value) => {
    setEditingLease((prev) => {
      const newTanks = [...(prev.tanks || [])];
      newTanks[index] = { ...newTanks[index], [field]: value };
      return { ...prev, tanks: newTanks };
    });
  };

  // -------------------------------
  // SEARCH & SORT (leases)
  // -------------------------------
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey)
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
  };

  const sortedAndFilteredLeases = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = (leases || []).filter((lease) => {
      const activePass = showInactive || lease.Active === "Y";

      const matchesSearch =
        (lease.LeaseID || "").toLowerCase().includes(lowerSearch) ||
        (lease.LeaseName || "").toLowerCase().includes(lowerSearch) ||
        (lease.RRC || "").toLowerCase().includes(lowerSearch) ||
        (lease.WellType || "").toLowerCase().includes(lowerSearch) ||
        (lease.PumperID || "").toLowerCase().includes(lowerSearch) ||
        (lease.ReliefID || "").toLowerCase().includes(lowerSearch) ||
        (lease.CompanyName || "").toLowerCase().includes(lowerSearch) ||
        (lease.Subdomain || "").toLowerCase().includes(lowerSearch) ||
        String(lease.PumperRoute ?? "")
          .toLowerCase()
          .includes(lowerSearch);

      return activePass && matchesSearch;
    });

    return filtered.sort((a, b) => {
      const valA = a[sortBy] ?? "";
      const valB = b[sortBy] ?? "";

      if (typeof valA === "string" && typeof valB === "string") {
        const comparison = valA.localeCompare(valB);
        return sortDirection === "asc" ? comparison : -comparison;
      } else {
        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }
    });
  }, [leases, searchTerm, sortBy, sortDirection, showInactive]);

  // -------------------------------
  // SEARCH & SORT (wells)
  // -------------------------------
  const handleWellSort = (columnKey) => {
    if (wellSortBy === columnKey)
      setWellSortDirection(wellSortDirection === "asc" ? "desc" : "asc");
    else {
      setWellSortBy(columnKey);
      setWellSortDirection("asc");
    }
  };

  const displayedWells = useMemo(() => {
    const arr = Array.isArray(editingLease?.wells) ? editingLease.wells : [];
    const lower = (wellSearch || "").toLowerCase();

    let rows = arr.map((well, idx) => ({ well, idx }));

    rows = rows.filter(({ well }) => {
      const activePass = showInactiveWells || (well.Active || "Y") === "Y";
      const matches =
        String(well.WellID || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.PropertyNum || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.RRCLeaseID || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.CP || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.ElectricMeter || "")
          .toLowerCase()
          .includes(lower);
      return activePass && matches;
    });

    rows.sort((a, b) => {
      const va = a.well?.[wellSortBy] ?? "";
      const vb = b.well?.[wellSortBy] ?? "";
      const sa = String(va);
      const sb = String(vb);
      const cmp = sa.localeCompare(sb);
      return wellSortDirection === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [
    editingLease?.wells,
    wellSearch,
    wellSortBy,
    wellSortDirection,
    showInactiveWells,
  ]);

  // -------------------------------
  // SEARCH & SORT (tanks)
  // -------------------------------
  const handleTankSort = (columnKey) => {
    if (tankSortBy === columnKey)
      setTankSortDirection(tankSortDirection === "asc" ? "desc" : "asc");
    else {
      setTankSortBy(columnKey);
      setTankSortDirection("asc");
    }
  };

  const displayedTanks = useMemo(() => {
    const arr = Array.isArray(editingLease?.tanks) ? editingLease.tanks : [];
    const lower = (tankSearch || "").toLowerCase();

    let rows = arr.map((tank, idx) => ({ tank, idx }));

    rows = rows.filter(({ tank }) => {
      const activePass = showInactiveTanks || (tank.Active || "Y") === "Y";
      const matches =
        String(tank.TankID || "")
          .toLowerCase()
          .includes(lower) ||
        String(tank.TankType || "")
          .toLowerCase()
          .includes(lower) ||
        String(tank.WPTankNum || "")
          .toLowerCase()
          .includes(lower);
      return activePass && matches;
    });

    rows.sort((a, b) => {
      const va = a.tank?.[tankSortBy] ?? "";
      const vb = b.tank?.[tankSortBy] ?? "";
      const sa = String(va);
      const sb = String(vb);
      const cmp = sa.localeCompare(sb);
      return tankSortDirection === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [
    editingLease?.tanks,
    tankSearch,
    tankSortBy,
    tankSortDirection,
    showInactiveTanks,
  ]);

  // -------------------------------
  // OPTIONS
  // -------------------------------
  const pumperOptions = validPumpers;
  const reliefOptions = validPumpers;
  const investorOptions = (users || []).filter((user) => user.Role === "I");

  // -------------------------------
  // Replace Pumper actions
  // -------------------------------
  const openReplacePumper = () => {
    setReplaceFromPumper("");
    setReplaceToPumper("");
    setShowReplacePumperModal(true);
  };

  const closeReplacePumper = () => {
    if (!replaceBusy) setShowReplacePumperModal(false);
  };

  const confirmReplacePumper = async () => {
    if (!replaceFromPumper || !replaceToPumper) {
      alert("Please choose both Current Pumper and New Pumper.");
      return;
    }
    if (replaceFromPumper === replaceToPumper) {
      alert("Current Pumper and New Pumper cannot be the same.");
      return;
    }

    const fromUser = (validPumpers || []).find(
      (u) => u.UserID === replaceFromPumper,
    );
    const toUser = (validPumpers || []).find(
      (u) => u.UserID === replaceToPumper,
    );
    const fromName = fromUser
      ? `${fromUser.UserID} - ${fromUser.FullName}`
      : replaceFromPumper;
    const toName = toUser
      ? `${toUser.UserID} - ${toUser.FullName}`
      : replaceToPumper;
    const localCount = (leases || []).filter(
      (l) => l.Active === "Y" && l.PumperID === replaceFromPumper,
    ).length;

    const sure = window.confirm(
      `Are you sure you want to move all ACTIVE leases from:\n\n` +
        `• ${fromName}\n` +
        `to\n` +
        `• ${toName}\n\n` +
        `About ${localCount} lease(s) appear to be affected locally. This cannot be undone.`,
    );
    if (!sure) return;

    try {
      setReplaceBusy(true);
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}`;
      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          replacePumper: true,
          currentPumper: replaceFromPumper,
          newPumper: replaceToPumper,
        }),
      });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Replace pumper failed: ${resp.status}. ${msg}`);
      }
      const data = await resp.json();

      setLeases((prev) =>
        prev.map((l) =>
          l.Active === "Y" && l.PumperID === replaceFromPumper
            ? { ...l, PumperID: replaceToPumper }
            : l,
        ),
      );

      alert(
        `Success. Moved all ACTIVE leases from ${replaceFromPumper} to ${replaceToPumper}.` +
          (typeof data.affectedLeases !== "undefined"
            ? ` Updated ${data.affectedLeases} lease(s).`
            : ""),
      );
      setShowReplacePumperModal(false);
    } catch (e) {
      console.error(e);
      alert("Failed to replace pumper. See console for details.");
    } finally {
      setReplaceBusy(false);
    }
  };

  // -------------------------------
  // RENDER
  // -------------------------------
  return (
    <div className="p-2">
      {/* Top bar: search & actions */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3">
        <div className="flex-1 flex items-center gap-3">
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className={`absolute left-3 top-2.5 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            />
            <input
              type="text"
              placeholder="Search leases..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? "bg-gray-700 text-gray-100 border border-gray-600"
                  : "bg-white text-gray-700 border border-gray-300"
              }`}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={openReplacePumper}
            className="inline-flex items-center px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white"
            title="Move all ACTIVE leases from one pumper to another"
          >
            Replace Pumper
          </button>

          <button
            onClick={handleCreateLease}
            className="inline-flex items-center px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            New
          </button>

          <button
            onClick={handleExportLeases}
            className="inline-flex items-center px-3 py-1 rounded bg-teal-500 hover:bg-teal-600 text-white"
          >
            <FontAwesomeIcon icon={faFileExport} className="mr-1" />
            Export
          </button>

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none ml-2">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span className={isDark ? "text-gray-200" : "text-gray-700"}>
              Show Inactive
            </span>
          </label>
        </div>
      </div>

      {/* Leases Table */}
      {sortedAndFilteredLeases && sortedAndFilteredLeases.length > 0 ? (
        <div className="overflow-x-auto rounded-md shadow-sm">
          <table className="min-w-full text-sm">
            <thead className={getHeaderClasses()}>
              <tr>
                <SortableHeader
                  label="LeaseID"
                  columnKey="LeaseID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="LeaseName"
                  columnKey="LeaseName"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="RRC #"
                  columnKey="RRC"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="WellType"
                  columnKey="WellType"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Pumper"
                  columnKey="PumperID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Route"
                  columnKey="PumperRoute"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Relief"
                  columnKey="ReliefID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Active"
                  columnKey="Active"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <th className="py-2 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredLeases.map((lease, idx) => (
                <tr key={idx} className={getRowClasses()}>
                  <td className="py-2 px-4">{lease.LeaseID}</td>
                  <td className="py-2 px-4">{lease.LeaseName}</td>
                  <td className="py-2 px-4">{lease.RRC}</td>
                  <td className="py-2 px-4">{lease.WellType}</td>
                  <td className="py-2 px-4">{lease.PumperID || ""}</td>
                  <td className="py-2 px-4">{lease.PumperRoute ?? ""}</td>
                  <td className="py-2 px-4">{lease.ReliefID || ""}</td>
                  <td className="py-2 px-4 text-right">
                    {lease.Active === "Y" ? <span title="Active">✓</span> : ""}
                  </td>
                  <td className="py-2 px-4">
                    <button
                      onClick={() => handleEditLease(lease)}
                      className="inline-flex items-center px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleCopyLease(lease)}
                      className="inline-flex items-center px-2 py-1 rounded bg-purple-500 hover:bg-purple-600 text-white ml-2"
                    >
                      <FontAwesomeIcon icon={faCopy} className="mr-1" />
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={`${isDark ? "text-gray-300" : "text-gray-500"} mt-4`}>
          No leases found.
        </p>
      )}

      {/* Lease Modal */}
      {showLeaseModal && editingLease && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 ${
            isDark ? "bg-black bg-opacity-80" : "bg-black bg-opacity-50"
          }`}
        >
          <div
            className={`w-full max-w-5xl p-4 rounded-lg shadow-2xl relative transition-colors duration-300 ${
              isDark ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
            }`}
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            {/* Sticky header */}
            <div
              className={`sticky top-0 z-10 px-4 py-3 border-b flex justify-between items-center ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <h4 className="font-semibold text-sm">
                {editingLease.LeaseID || "(New)"} –{" "}
                {editingLease.LeaseName || "Unnamed Lease"}
              </h4>
              <button
                onClick={handleCancelLease}
                className="p-2 rounded-full hover:bg-gray-500/20"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            {/* Modal Title */}
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-bold">
                {editingLease.LeaseID &&
                leases.some((l) => l.LeaseID === editingLease.LeaseID)
                  ? "Edit Lease"
                  : "Create Lease"}
              </h4>
              <button
                onClick={handleCancelLease}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            {/* Tab Buttons */}
            {(() => {
              const isInjection =
                (editingLease.WellType || "").toUpperCase() === "INJ";
              return (
                <div className="mb-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveTab("basic")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "basic"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Basic Info
                  </button>

                  <button
                    onClick={() => setActiveTab("additional")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "additional"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Additional Info
                  </button>

                  {!isInjection && (
                    <button
                      onClick={() => setActiveTab("purchasers")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "purchasers"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Purchasers
                    </button>
                  )}

                  {isInjection && (
                    <button
                      onClick={() => setActiveTab("injection")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "injection"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Injection
                    </button>
                  )}

                  <button
                    onClick={() => setActiveTab("wells")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "wells"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Wells
                  </button>

                  {!isInjection && (
                    <button
                      onClick={() => setActiveTab("tanks")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "tanks"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Tanks
                    </button>
                  )}
                </div>
              );
            })()}
            {/* BASIC */}
            {activeTab === "basic" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                <div>
                  <label className="block mb-1 font-semibold">Lease ID</label>
                  <input
                    name="LeaseID"
                    value={editingLease.LeaseID}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    readOnly={isEditMode}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Lease Name</label>
                  <input
                    name="LeaseName"
                    value={editingLease.LeaseName}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Active?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="leaseActiveToggle"
                      type="checkbox"
                      name="Active"
                      checked={editingLease.Active === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="leaseActiveToggle" className="font-medium">
                      Enabled
                    </label>
                  </div>
                </div>

                {/* Pumper, Route */}
                <div>
                  <label className="block mb-1 font-semibold">Pumper</label>
                  <select
                    name="PumperID"
                    value={editingLease.PumperID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {pumperOptions
                      ?.filter((u) => u.UserID !== "admin")
                      .map((u) => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.UserID} - {u.FullName}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    Pumper Route
                  </label>
                  <input
                    type="number"
                    name="PumperRoute"
                    min="0"
                    step="1"
                    value={editingLease.PumperRoute ?? ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g. 1, 2, 3…"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>

                {/* Relief */}
                <div>
                  <label className="block mb-1 font-semibold">Relief</label>
                  <select
                    name="ReliefID"
                    value={editingLease.ReliefID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {reliefOptions
                      ?.filter((u) => u.UserID !== "admin")
                      .map((u) => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.UserID} - {u.FullName}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Group, WellType */}
                <div>
                  <label className="block mb-1 font-semibold">Group</label>
                  <input
                    type="text"
                    name="GroupID"
                    value={editingLease.GroupID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">WellType</label>
                  <select
                    name="WellType"
                    value={editingLease.WellType || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="OIL">OIL</option>
                    <option value="GAS">GAS</option>
                    <option value="INJ">INJ</option>
                    <option value="WSW">WSW</option>
                  </select>
                </div>

                {/* Show flags */}
                <div>
                  <label className="block mb-1 font-semibold">Show Oil?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showOilToggle"
                      type="checkbox"
                      name="ShowOil"
                      checked={editingLease.ShowOil === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showOilToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Show Gas?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showGasToggle"
                      type="checkbox"
                      name="ShowGas"
                      checked={editingLease.ShowGas === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showGasToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    Show Water?
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showWaterToggle"
                      type="checkbox"
                      name="ShowWater"
                      checked={editingLease.ShowWater === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showWaterToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                {/* Water Meter, District */}
                <div>
                  <label className="block mb-1 font-semibold">
                    Use Water Meter?
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="useWaterMeter"
                      type="checkbox"
                      name="WaterMeter"
                      checked={editingLease.WaterMeter === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="useWaterMeter" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">District</label>
                  <input
                    name="District"
                    value={editingLease.District || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* RRC, CP, County, FieldName */}
                <div>
                  <label className="block mb-1 font-semibold">RRC</label>
                  <input
                    name="RRC"
                    value={editingLease.RRC || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">CP</label>
                  <input
                    name="CP"
                    value={editingLease.CP || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">County</label>
                  <input
                    name="County"
                    value={editingLease.County || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g., Howard"
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Field Name</label>
                  <input
                    name="FieldName"
                    value={editingLease.FieldName || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g., Spraberry"
                  />
                </div>
              </div>
            )}
            {/* ADDITIONAL (Tags/Investors/External Property #) */}
            {activeTab === "additional" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                <div>
                  <label className="block mb-1 font-semibold">Tag 1</label>
                  <select
                    name="Tag1"
                    value={editingLease.Tag1 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 2</label>
                  <select
                    name="Tag2"
                    value={editingLease.Tag2 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 3</label>
                  <select
                    name="Tag3"
                    value={editingLease.Tag3 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 4</label>
                  <select
                    name="Tag4"
                    value={editingLease.Tag4 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Investor 1</label>
                  <select
                    name="Investor1"
                    value={editingLease.Investor1 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {investorOptions?.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.UserID} - {u.FullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Investor 2</label>
                  <select
                    name="Investor2"
                    value={editingLease.Investor2 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {investorOptions?.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.UserID} - {u.FullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    External Property #
                  </label>
                  <input
                    name="PropertyNum"
                    value={editingLease.PropertyNum || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>
              </div>
            )}
            {/* PURCHASERS (hidden for INJ) */}
            {activeTab === "purchasers" &&
              (editingLease.WellType || "").toUpperCase() !== "INJ" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                  <div>
                    <label className="block mb-1 font-semibold">
                      Purchaser
                    </label>
                    <select
                      name="Purchaser"
                      value={editingLease.Purchaser || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                    >
                      <option value="">-- Select --</option>
                      {purchasers?.map((p) => (
                        <option key={p.PurchaserID} value={p.PurchaserID}>
                          {p.PurchaserID} - {p.PurchaserName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Purchaser Lease No
                    </label>
                    <input
                      name="PurchaserLeaseNo"
                      value={editingLease.PurchaserLeaseNo || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Water Transport
                    </label>
                    <input
                      name="WaterTransport"
                      value={editingLease.WaterTransport || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="e.g., ABC Water Hauling"
                    />
                  </div>
                </div>
              )}
            {/* INJECTION (only when WellType == INJ) */}
            {activeTab === "injection" &&
              (editingLease.WellType || "").toUpperCase() === "INJ" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                  <div>
                    <label className="block mb-1 font-semibold">UIC</label>
                    <input
                      id="uicInput"
                      type="text"
                      name="UIC"
                      value={editingLease.UIC || ""}
                      onChange={handleLeaseFieldChange}
                      maxLength={9}
                      className={getInputClasses(isDark)}
                      placeholder="e.g., 123456789"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Daily Target
                    </label>
                    <input
                      type="number"
                      name="Target"
                      min="0"
                      step="1"
                      value={editingLease.Target ?? 0}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="0"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      title="Integer daily target (e.g., 0, 25, 300)"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">Max Inj</label>
                    <input
                      type="number"
                      name="MaxInj"
                      min="0"
                      step="1"
                      value={editingLease.MaxInj ?? 0}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="0"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Max Pressure
                    </label>
                    <input
                      type="number"
                      name="MaxPressure"
                      min="0"
                      step="1"
                      value={editingLease.MaxPressure ?? 0}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="0"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>
                </div>
              )}
            {/* WELLS (grid-style like leases) */}
            {activeTab === "wells" && (
              <div className="mb-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mb-2">
                  <h5 className="font-semibold">Wells</h5>

                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <div className="relative">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className={`absolute left-3 top-2.5 ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                      <input
                        type="text"
                        placeholder="Search wells..."
                        value={wellSearch}
                        onChange={(e) => setWellSearch(e.target.value)}
                        className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark
                            ? "bg-gray-700 text-gray-100 border border-gray-600"
                            : "bg-white text-gray-700 border border-gray-300"
                        }`}
                      />
                    </div>

                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={showInactiveWells === true}
                        onChange={(e) => setShowInactiveWells(e.target.checked)}
                      />
                      <span
                        className={isDark ? "text-gray-200" : "text-gray-700"}
                      >
                        Show Inactive
                      </span>
                    </label>

                    <button
                      onClick={handleAddWell}
                      className="inline-flex items-center px-2 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800 whitespace-nowrap"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-1" />
                      Add Well
                    </button>
                  </div>
                </div>

                {displayedWells.length > 0 ? (
                  <div className="w-full rounded-md shadow-sm overflow-hidden">
                    <table className="w-full table-auto text-sm">
                      <thead className={getHeaderClasses()}>
                        <tr>
                          <SortableHeader
                            label="WellID"
                            columnKey="WellID"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Active"
                            columnKey="Active"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="External Well #"
                            columnKey="PropertyNum"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Fixed Alloc Pct"
                            columnKey="AllocPct"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="API #"
                            columnKey="API"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="RRC"
                            columnKey="RRCLeaseID"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Electric Meter #"
                            columnKey="ElectricMeter"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="CP #"
                            columnKey="CP"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <th className="py-2 px-2 text-left whitespace-nowrap">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayedWells.map(({ well, idx }) => (
                          <tr
                            key={well.UniqID || `well-${idx}`}
                            className={getRowClasses()}
                          >
                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.WellID || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "WellID",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <select
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.Active || "Y"}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "Active",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="Y">Yes</option>
                                <option value="N">No</option>
                              </select>
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.PropertyNum || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "PropertyNum",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.AllocPct || "0.0000"}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "AllocPct",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(
                                  isDark,
                                )} w-[11ch] max-w-full min-w-0 whitespace-nowrap`}
                                value={well.API || ""}
                                maxLength={11}
                                inputMode="numeric"
                                pattern="\d*"
                                onChange={(e) =>
                                  handleWellChange(idx, "API", e.target.value)
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.RRCLeaseID || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "RRCLeaseID",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.ElectricMeter || ""}
                                maxLength={50}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "ElectricMeter",
                                    e.target.value,
                                  )
                                }
                                placeholder="Meter #"
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.CP || ""}
                                onChange={(e) =>
                                  handleWellChange(idx, "CP", e.target.value)
                                }
                              />
                            </td>

                            <td className="py-2 px-2 whitespace-nowrap">
                              <button
                                onClick={() => handleRemoveWell(well)}
                                className="inline-flex items-center px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs whitespace-nowrap"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="mr-1"
                                />
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {toast.visible && (
                      <div className="fixed inset-0 flex items-center justify-center z-50">
                        <div className="bg-red-500 text-white p-4 rounded shadow-lg">
                          <div className="flex items-center justify-between">
                            <span>{toast.message}</span>
                            <button
                              onClick={() =>
                                setToast({ ...toast, visible: false })
                              }
                              className="ml-4"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm italic">No wells found.</p>
                )}
              </div>
            )}
            {/* TANKS (grid-style like leases) — hidden for INJ */}
            {activeTab === "tanks" &&
              (editingLease.WellType || "").toUpperCase() !== "INJ" && (
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h5 className="font-semibold">Tanks</h5>

                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faSearch}
                          className={`absolute left-3 top-2.5 ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        />
                        <input
                          type="text"
                          placeholder="Search tanks..."
                          value={tankSearch}
                          onChange={(e) => setTankSearch(e.target.value)}
                          className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 text-gray-100 border border-gray-600"
                              : "bg-white text-gray-700 border border-gray-300"
                          }`}
                        />
                      </div>

                      <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showInactiveTanks === true}
                          onChange={(e) =>
                            setShowInactiveTanks(e.target.checked)
                          }
                        />
                        <span
                          className={isDark ? "text-gray-200" : "text-gray-700"}
                        >
                          Show Inactive
                        </span>
                      </label>

                      <button
                        onClick={handleAddTank}
                        className="inline-flex items-center px-2 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800"
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                        Add Tank
                      </button>
                    </div>
                  </div>

                  {displayedTanks.length > 0 ? (
                    <div className="overflow-x-auto rounded-md shadow-sm">
                      <table className="min-w-full text-sm">
                        <thead className={getHeaderClasses()}>
                          <tr>
                            <SortableHeader
                              label="TankID"
                              columnKey="TankID"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Active"
                              columnKey="Active"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Size"
                              columnKey="Size"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="BBLS/Inch"
                              columnKey="BBLSperInch"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Type"
                              columnKey="TankType"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="GasCoeff"
                              columnKey="GasCoeff"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Exclude Draws"
                              columnKey="ExcludeDrawsFromProd"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="External Tank #"
                              columnKey="WPTankNum"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <th className="py-2 px-4 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedTanks.map(({ tank, idx }) => (
                            <tr
                              key={tank.UniqID || `tank-${idx}`}
                              className={getRowClasses()}
                            >
                              <td className="py-2 px-4">
                                <input
                                  className={getInputClasses(isDark)}
                                  value={tank.TankID || ""}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "TankID",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={`${getInputClasses(
                                    isDark,
                                  )} min-w-[5.25rem]`}
                                  value={tank.Active || "Y"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "Active",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="Y">Yes</option>
                                  <option value="N">No</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  className={getInputClasses(isDark)}
                                  value={tank.Size ?? 0}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "Size",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="0.001"
                                  className={getInputClasses(isDark)}
                                  value={tank.BBLSperInch || "0.000"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "BBLSperInch",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={`${getInputClasses(
                                    isDark,
                                  )} min-w-[5.75rem]`}
                                  value={tank.TankType || "T"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "TankType",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="T">Tank</option>
                                  <option value="W">Water</option>
                                  <option value="F">Frac</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  className={getInputClasses(isDark)}
                                  value={tank.GasCoeff || "0.00"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "GasCoeff",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={getInputClasses(isDark)}
                                  value={tank.ExcludeDrawsFromProd || "N"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "ExcludeDrawsFromProd",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="N">No</option>
                                  <option value="Y">Yes</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  className={getInputClasses(isDark)}
                                  value={tank.WPTankNum || ""}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "WPTankNum",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <button
                                  onClick={() => {
                                    const hasHistory =
                                      tank?.HasHistory === true ||
                                      (typeof tank?.HasHistory === "string" &&
                                        tank.HasHistory.toUpperCase() ===
                                          "Y") ||
                                      Number(
                                        tank?.HistoryCount ??
                                          tank?.HistoricalCount ??
                                          tank?.ProdCount ??
                                          tank?.DrawCount ??
                                          0,
                                      ) > 0;

                                    if (hasHistory) {
                                      setToast({
                                        visible: true,
                                        message:
                                          "Cannot remove this tank because it has historical data. Please set Active to 'No' instead.",
                                      });
                                      return;
                                    }

                                    handleRemoveTank(tank);
                                  }}
                                  className="inline-flex items-center px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs"
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="mr-1"
                                  />
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {toast.visible && (
                        <div className="fixed inset-0 flex items-center justify-center z-50">
                          <div className="bg-red-500 text-white p-4 rounded shadow-lg">
                            <div className="flex items-center justify-between">
                              <span>{toast.message}</span>
                              <button
                                onClick={() =>
                                  setToast({ ...toast, visible: false })
                                }
                                className="ml-4"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm italic">No tanks found.</p>
                  )}
                </div>
              )}
            {/* Footer */}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={handleCancelLease}
                className={`px-3 py-1 rounded ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLease}
                className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                Save Lease
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Pumper Modal */}
      {showReplacePumperModal && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-50 ${
            isDark ? "bg-black bg-opacity-80" : "bg-black bg-opacity-40"
          }`}
        >
          <div
            className={`w-full max-w-md p-4 rounded-lg shadow-2xl relative ${
              isDark ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
            }`}
          >
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-bold">Replace Pumper</h4>
              <button
                onClick={closeReplacePumper}
                className="p-2 rounded-full hover:bg-gray-500/20"
                disabled={replaceBusy}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <p className="text-sm mb-3">
              This will <strong>move all ACTIVE leases</strong> assigned to the
              current pumper to the new pumper.
            </p>

            <div className="grid grid-cols-1 gap-3 text-sm">
              <div>
                <label className="block mb-1 font-semibold">
                  Current Pumper
                </label>
                <select
                  value={replaceFromPumper}
                  onChange={(e) => setReplaceFromPumper(e.target.value)}
                  className={getInputClasses(isDark)}
                  disabled={replaceBusy}
                >
                  <option value="">-- Select --</option>
                  {pumperOptions?.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {u.UserID} - {u.FullName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 font-semibold">New Pumper</label>
                <select
                  value={replaceToPumper}
                  onChange={(e) => setReplaceToPumper(e.target.value)}
                  className={getInputClasses(isDark)}
                  disabled={replaceBusy}
                >
                  <option value="">-- Select --</option>
                  {pumperOptions?.map((u) => (
                    <option key={u.UserID} value={u.UserID}>
                      {u.UserID} - {u.FullName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={closeReplacePumper}
                className={`px-3 py-1 rounded ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
                disabled={replaceBusy}
              >
                Cancel
              </button>
              <button
                onClick={confirmReplacePumper}
                className="px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-70"
                disabled={replaceBusy}
              >
                {replaceBusy ? "Replacing..." : "Replace"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SortableHeader is a reusable table-header cell for sorting.
 * It shows an up/down icon if the column is currently sorted.
 */
function SortableHeader({
  label,
  columnKey,
  sortBy,
  sortDirection,
  onSort,
  theme,
}) {
  const isSortedColumn = sortBy === columnKey;
  const isDark = theme === "dark";

  return (
    <th
      className="py-2 px-4 text-left cursor-pointer select-none"
      onClick={() => onSort(columnKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {isSortedColumn && (
          <FontAwesomeIcon
            icon={sortDirection === "asc" ? faSortUp : faSortDown}
            className={isDark ? "text-gray-200" : "text-gray-600"}
          />
        )}
      </div>
    </th>
  );
}
